import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Music, List, CalendarDays, Filter, X, Star, Navigation, Loader2 } from 'lucide-react';
import { useUnifiedSearch, useNearbySearch, useSearchEvents, type EventWithBand } from '../../hooks/useSearch';
import { getInitials, formatTime, GENRE_OPTIONS } from '../../lib/utils';
import { COUNTRIES } from '../../lib/countries';
import CalendarView from '../../components/ui/CalendarView';
import { useGeolocation } from '../../hooks/useGeolocation';
import type { Band } from '../../types/database';

type ViewMode = 'list' | 'calendar';
type CalendarMode = 'month' | 'week' | 'day';

function EventList({ events }: { events: EventWithBand[] }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Upcoming Gigs ({events.length})
      </h2>
      <div className="grid gap-3">
        {events.map((event) => (
          <Link
            key={event.id}
            to={`/band/${event.band?.slug}`}
            className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-indigo-500/40 transition group"
          >
            <div className="flex items-start gap-4">
              <div className="bg-indigo-600/15 border border-indigo-500/25 rounded-xl px-3 py-2 text-center flex-shrink-0 min-w-[70px]">
                <div className="text-indigo-400 text-[10px] font-semibold uppercase">
                  {new Date(event.start_time).toLocaleDateString('en-GB', { month: 'short' })}
                </div>
                <div className="text-gray-50 text-xl font-bold leading-tight">
                  {new Date(event.start_time).getDate()}
                </div>
                <div className="text-indigo-400 text-[10px]">
                  {new Date(event.start_time).toLocaleDateString('en-GB', { weekday: 'short' })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold group-hover:text-indigo-400 transition">{event.title}</h3>
                <div className="flex items-center gap-1 text-indigo-400 text-sm mt-0.5">
                  <Music className="w-3 h-3" />
                  {event.band?.name}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-400 text-sm mt-1">
                  <span>{formatTime(event.start_time)}</span>
                  {event.venue_name && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {event.venue_name}
                    </span>
                  )}
                </div>
                {event.description && (
                  <p className="text-gray-500 text-sm mt-1.5 line-clamp-2">{event.description}</p>
                )}
                {event.ticket_url && (
                  <span className="inline-block mt-1.5 text-indigo-400 text-xs font-medium">
                    Tickets available {event.ticket_price && `(${event.ticket_price})`}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [showEvents, setShowEvents] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { bands: textBands, loading: bandsLoading, searched, search } = useUnifiedSearch();
  const { bands: nearbyBands, loading: nearbyLoading, searched: nearbySearced, locationName, searchNearby } = useNearbySearch();
  const { events, loading: eventsLoading, fetchByBandIds, fetchAll } = useSearchEvents();
  const { loading: geoLoading, error: geoError, requestLocation } = useGeolocation();
  const [geoErrorVisible, setGeoErrorVisible] = useState(false);
  const [nearbyRadius, setNearbyRadius] = useState(100);

  // Determine if we're in nearby mode
  const isNearbyMode = !!(searchParams.get('lat') && searchParams.get('lng'));
  const bands: Band[] = isNearbyMode ? nearbyBands : textBands;
  const isSearched = isNearbyMode ? nearbySearced : searched;

  // Debounce search input
  useEffect(() => {
    if (isNearbyMode) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, isNearbyMode]);

  // Trigger text search on debounced query change
  useEffect(() => {
    if (isNearbyMode) return;
    if (debouncedQuery.trim()) {
      search(debouncedQuery);
      setSearchParams({ q: debouncedQuery }, { replace: true });
    } else if (debouncedQuery === '') {
      search('');
    }
  }, [debouncedQuery, isNearbyMode]);

  // Trigger nearby search when lat/lng params are present
  useEffect(() => {
    const lat = parseFloat(searchParams.get('lat') || '');
    const lng = parseFloat(searchParams.get('lng') || '');
    if (!isNaN(lat) && !isNaN(lng)) {
      searchNearby(lat, lng, nearbyRadius);
    }
  }, [searchParams.get('lat'), searchParams.get('lng'), nearbyRadius]);

  // Fetch events when bands change
  useEffect(() => {
    if (bands.length > 0) {
      fetchByBandIds(bands.map((b) => b.id));
    } else if (isSearched && bands.length === 0) {
      fetchByBandIds([]);
    } else if (!isSearched) {
      fetchAll();
    }
  }, [bands, isSearched]);

  // On initial load with query param
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q !== query) {
      setQuery(q);
      setDebouncedQuery(q);
    }
  }, []);

  async function handleNearMe() {
    const coords = await requestLocation();
    if (coords) {
      setQuery('');
      setDebouncedQuery('');
      setSearchParams({ lat: String(coords.latitude), lng: String(coords.longitude) }, { replace: true });
    } else {
      setGeoErrorVisible(true);
      setTimeout(() => setGeoErrorVisible(false), 5000);
    }
  }

  function exitNearbyMode() {
    setSearchParams({}, { replace: true });
    search('');
  }

  // Detect if query matches a country
  const matchedCountry = COUNTRIES.find(
    (c) => c.name.toLowerCase() === debouncedQuery.trim().toLowerCase()
  );

  // Filter bands by selected genres
  const filteredBands = selectedGenres.length > 0
    ? bands.filter((b) => b.genres.some((g) => selectedGenres.includes(g)))
    : bands;

  // Filter events by filtered bands
  const filteredEvents = selectedGenres.length > 0
    ? events.filter((e) => filteredBands.some((b) => b.id === e.band_id))
    : events;

  function toggleGenre(genre: string) {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
  }

  const loading = bandsLoading || nearbyLoading || eventsLoading;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Search header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-4">Discover Bands & Gigs</h1>

        {/* Search input with live results */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (isNearbyMode) exitNearbyMode();
              }}
              placeholder="Search bands, cities, countries... e.g. Rock, London, Guernsey"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-12 pr-10 py-3.5 text-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg"
            />
            {(query || isNearbyMode) && (
              <button
                onClick={() => { setQuery(''); setDebouncedQuery(''); if (isNearbyMode) exitNearbyMode(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-50 transition"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleNearMe}
            disabled={geoLoading}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition flex-shrink-0 ${
              isNearbyMode
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-800 border border-gray-700 text-gray-300 hover:text-gray-50 hover:border-indigo-500/50'
            } disabled:opacity-50`}
            title="Search bands near your location"
          >
            {geoLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Navigation className="w-5 h-5" />
            )}
            <span className="hidden sm:inline">Near Me</span>
          </button>
        </div>

        {/* Geolocation error */}
        {geoErrorVisible && geoError && (
          <div className="mt-2 text-sm text-red-400 bg-red-900/30 border border-red-700/40 rounded-lg px-4 py-2">
            {geoError}
          </div>
        )}
      </div>

      {/* Nearby location banner */}
      {isNearbyMode && isSearched && (
        <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/30 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-600/20 p-2.5 rounded-xl">
                <Navigation className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  {locationName ? `Bands near ${locationName}` : 'Bands Near You'}
                </h2>
                <p className="text-gray-400 text-sm">
                  {bands.length} band{bands.length !== 1 ? 's' : ''} within {nearbyRadius} km
                  {' '}&middot;{' '}
                  {filteredEvents.length} upcoming gig{filteredEvents.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={nearbyRadius}
                onChange={(e) => setNearbyRadius(Number(e.target.value))}
                className="bg-gray-800 border border-gray-700 text-sm text-gray-50 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value={25}>25 km</option>
                <option value={50}>50 km</option>
                <option value={100}>100 km</option>
                <option value={250}>250 km</option>
                <option value={500}>500 km</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Country banner */}
      {matchedCountry && filteredBands.length > 0 && (
        <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{matchedCountry.flag}</span>
            <div>
              <h2 className="text-xl font-bold">{matchedCountry.name}</h2>
              <p className="text-gray-400 text-sm">
                {filteredBands.length} band{filteredBands.length !== 1 ? 's' : ''} &middot;{' '}
                {filteredEvents.length} upcoming gig{filteredEvents.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar: View mode + Filters - always visible */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-800 rounded-lg p-0.5 text-sm">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                viewMode === 'calendar' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Calendar
            </button>
          </div>

          {/* Show/hide band cards in list mode (only when searching) */}
          {viewMode === 'list' && isSearched && filteredBands.length > 0 && (
            <button
              onClick={() => setShowEvents(!showEvents)}
              className={`text-sm px-3 py-1.5 rounded-lg transition ${
                showEvents ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {showEvents ? 'Hide Gigs' : 'Show Gigs'}
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition ${
            showFilters || selectedGenres.length > 0
              ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30'
              : 'bg-gray-800 text-gray-400 hover:text-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
          {selectedGenres.length > 0 && (
            <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full ml-1">
              {selectedGenres.length}
            </span>
          )}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-300">Filter by Genre</h3>
            {selectedGenres.length > 0 && (
              <button
                onClick={() => setSelectedGenres([])}
                className="text-xs text-indigo-400 hover:text-indigo-300"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {GENRE_OPTIONS.map((genre) => (
              <button
                key={genre}
                onClick={() => toggleGenre(genre)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  selectedGenres.includes(genre)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-gray-50 hover:bg-gray-700'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
        </div>
      )}

      {/* No results */}
      {!loading && isSearched && filteredBands.length === 0 && (
        <div className="text-center py-16">
          <Music className="w-14 h-14 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">
            {isNearbyMode
              ? `No bands found within ${nearbyRadius} km of your location`
              : `No bands found for "${debouncedQuery}"`}
          </p>
          <p className="text-gray-500 mt-2 text-sm">
            {isNearbyMode
              ? 'Try increasing the search radius.'
              : 'Try a different search term or clear your filters.'}
          </p>
        </div>
      )}

      {/* Search Results */}
      {!loading && isSearched && filteredBands.length > 0 && (
        <>
          {/* List View */}
          {viewMode === 'list' && (
            <div>
              {/* Bands section */}
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Bands ({filteredBands.length})
                </h2>
                <div className="grid gap-3">
                  {filteredBands.map((band) => (
                    <Link
                      key={band.id}
                      to={`/band/${band.slug}`}
                      className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-indigo-500/40 transition group"
                    >
                      <div className="flex items-start gap-4">
                        {band.logo_url ? (
                          <img src={band.logo_url} alt={band.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-750">
                            <span className="text-gray-400 font-bold">{getInitials(band.name)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold group-hover:text-indigo-400 transition">{band.name}</h3>
                            {band.priority_rank > 0 && (
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                            )}
                          </div>
                          {band.location_name && (
                            <div className="flex items-center gap-1 text-gray-400 text-sm mt-0.5">
                              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                              {band.location_name}
                              {isNearbyMode && 'distance_km' in band && (
                                <span className="text-emerald-400 ml-1">
                                  ({Math.round((band as Band & { distance_km: number }).distance_km)} km)
                                </span>
                              )}
                            </div>
                          )}
                          {band.genres.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {band.genres.map((genre) => (
                                <span key={genre} className="bg-gray-800 text-gray-300 text-[11px] px-2 py-0.5 rounded-full">
                                  {genre}
                                </span>
                              ))}
                            </div>
                          )}
                          {band.description && (
                            <p className="text-gray-500 text-sm mt-2 line-clamp-2">{band.description}</p>
                          )}
                        </div>
                        {/* Upcoming gig count for this band */}
                        {(() => {
                          const count = filteredEvents.filter((e) => e.band_id === band.id).length;
                          return count > 0 ? (
                            <div className="hidden sm:flex flex-col items-center flex-shrink-0 bg-indigo-900/30 rounded-lg px-3 py-2">
                              <span className="text-indigo-300 text-lg font-bold">{count}</span>
                              <span className="text-indigo-400 text-[10px] uppercase">gig{count !== 1 ? 's' : ''}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Events section */}
              {showEvents && filteredEvents.length > 0 && (
                <EventList events={filteredEvents} />
              )}
            </div>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <CalendarView
              events={filteredEvents}
              view={calendarMode}
              onViewChange={setCalendarMode}
            />
          )}
        </>
      )}

      {/* Default state - no search query: show all upcoming gigs */}
      {!loading && !isSearched && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            All Upcoming Gigs
          </h2>
          {filteredEvents.length === 0 ? (
            <div className="text-center py-16">
              <CalendarDays className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-400">No upcoming gigs found.</p>
              <p className="text-gray-500 text-sm mt-1">Try searching for a band or location above.</p>
            </div>
          ) : viewMode === 'calendar' ? (
            <CalendarView
              events={filteredEvents}
              view={calendarMode}
              onViewChange={setCalendarMode}
            />
          ) : (
            <EventList events={filteredEvents} />
          )}
        </div>
      )}
    </div>
  );
}
